import React, { useState, useEffect } from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";

interface SignalTpSlSettingsProps {
  botData: any;
  updateSettings: (key: string, value: any) => void;
}

export const SignalTpSlSettings: React.FC<SignalTpSlSettingsProps> = ({
  botData,
  updateSettings
}) => {
  const { settings, strategy_type } = botData;

  const [symbolText, setSymbolText] = useState(() => {
    const obj = settings.symbol_tp_sl || {};
    return Object.entries(obj)
      .map(([sym, cfg]: [string, any]) => `${sym}: ${cfg.tp_pct}/${cfg.sl_pct}`)
      .join('\n');
  });

  const handleSymbolTextChange = (val: string) => {
    setSymbolText(val);
    const lines = val.split('\n');
    const newObj: Record<string, { tp_pct: number; sl_pct: number }> = {};
    lines.forEach(line => {
      const parts = line.split(':');
      if (parts.length === 2) {
        const sym = parts[0].trim().toUpperCase();
        const values = parts[1].split('/');
        if (sym && values.length === 2) {
          const tp = parseFloat(values[0]);
          const sl = parseFloat(values[1]);
          if (!isNaN(tp) && !isNaN(sl)) {
            newObj[sym] = { tp_pct: tp, sl_pct: sl };
          }
        }
      }
    });
    updateSettings('symbol_tp_sl', newObj);
  };

  useEffect(() => {
    const obj = settings.symbol_tp_sl || {};
    const text = Object.entries(obj)
      .map(([sym, cfg]: [string, any]) => `${sym}: ${cfg.tp_pct}/${cfg.sl_pct}`)
      .join('\n');
    if (text !== symbolText) {
      setSymbolText(text);
    }
  }, [settings.symbol_tp_sl]);

  return (
    <div className="flex flex-col p-3 border border-success/30 rounded-lg bg-success/5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label className="text-xs font-bold text-success">점진적 분할 익절 (Gradual TP)</Label>
          <p className="text-[10px] text-muted-foreground">4단계에 걸쳐 수량을 나눠 익절합니다</p>
        </div>
        <Checkbox
          checked={settings.use_gradual_tp !== false}
          onCheckedChange={(checked) => updateSettings('use_gradual_tp', checked)}
          className="border-success/50 data-[state=checked]:bg-success data-[state=checked]:border-success"
        />
      </div>

      {(settings.use_gradual_tp !== false) && (
        <div className="space-y-4 pt-2 border-t border-border/50">
          <p className="text-[9px] text-muted-foreground leading-relaxed px-1">
            💡 목표가(TP%)의 각 비율(50/75/100%)에 도달 시 설정된 비중만큼 분할 익절합니다. 1차 익절 후 본절 방어가 자동 활성화됩니다.
          </p>
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">목표가 도달 비율 (%) — 3단계</Label>
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map(i => {
                let currentLevels = settings.gradual_tp_levels || [50, 75, 100];
                if (currentLevels.length === 4) {
                  currentLevels = [50, 75, 100];
                }
                return (
                  <div key={i} className="space-y-1">
                    <span className="text-[9px] font-bold text-muted-foreground">{i + 1}차</span>
                    <Input
                      type="number"
                      value={currentLevels[i] ?? [50, 75, 100][i]}
                      onChange={(e) => {
                        const arr = [...currentLevels];
                        arr[i] = Number(e.target.value);
                        updateSettings('gradual_tp_levels', arr);
                      }}
                      className="h-8 text-xs font-bold border-border rounded-lg bg-background"
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">단계별 청산 비중 (%) — 합계 100%</Label>
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map(i => {
                let currentRatios = settings.gradual_tp_exit_ratios || [50, 25, 25];
                if (currentRatios.length === 4) {
                  currentRatios = [50, 25, 25];
                }
                return (
                  <div key={i} className="space-y-1">
                    <span className="text-[9px] font-bold text-muted-foreground">{i + 1}차</span>
                    <Input
                      type="number"
                      value={currentRatios[i] ?? [50, 25, 25][i]}
                      onChange={(e) => {
                        const arr = [...currentRatios];
                        arr[i] = Number(e.target.value);
                        updateSettings('gradual_tp_exit_ratios', arr);
                      }}
                      className="h-8 text-xs font-bold border-border rounded-lg bg-background"
                    />
                  </div>
                );
              })}
            </div>
            <p className="text-[9px] text-muted-foreground px-1">
              {(() => {
                let currentRatios = settings.gradual_tp_exit_ratios || [50, 25, 25];
                if (currentRatios.length === 4) currentRatios = [50, 25, 25];
                return `합계: ${currentRatios.reduce((a: number, b: number) => a + Number(b), 0).toFixed(1)}%`;
              })()}
              {(() => {
                let currentRatios = settings.gradual_tp_exit_ratios || [50, 25, 25];
                if (currentRatios.length === 4) currentRatios = [50, 25, 25];
                const sum = currentRatios.reduce((a: number, b: number) => a + Number(b), 0);
                return Math.abs(sum - 100) > 0.1 ? <span className="text-danger ml-1">⚠ 합계가 100%가 아닙니다</span> : null;
              })()}
            </p>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
            <div className="space-y-0.5">
              <Label className="text-[11px] font-bold">1차 익절 후 본절 방어</Label>
              <p className="text-[9px] text-muted-foreground">1차 TP 달성 시 손절가를 진입가로 이동</p>
            </div>
            <Checkbox
              checked={settings.breakeven_after_first_tp !== false}
              onCheckedChange={(checked) => updateSettings('breakeven_after_first_tp', checked)}
            />
          </div>
        </div>
      )}

      {/* 1차 TP 후 본절 SL 보호 */}
      <div className="flex items-center justify-between p-3 border border-info/30 rounded-lg bg-info/5 cursor-pointer" onClick={() => updateSettings('use_breakeven_protection', settings.use_breakeven_protection === false)}>
        <div className="space-y-0.5">
          <Label className="text-xs font-bold cursor-pointer">1차 TP 후 본절 SL 보호</Label>
          <p className="text-[10px] text-muted-foreground">첫 분할 익절 시 SL을 진입가 근처(±0.05%)로 이동</p>
        </div>
        <input type="checkbox" className="w-4 h-4 rounded border-info/50 text-info cursor-pointer" checked={settings.use_breakeven_protection !== false} readOnly />
      </div>

      {/* Dynamic Trailing Stop Option */}
      <div className="flex flex-col p-3 border border-warning/30 rounded-lg bg-warning/5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-xs font-bold text-warning">다이내믹 트레일링 스탑 (Trailing Trend Exit)</Label>
            <p className="text-[10px] text-muted-foreground">목표가 도달 시 바로 청산하지 않고 추세를 끝까지 쫓아갑니다</p>
          </div>
          <Checkbox
            checked={settings.use_trailing_trend_exit === true}
            onCheckedChange={(checked) => updateSettings('use_trailing_trend_exit', checked)}
            className="border-warning/50 data-[state=checked]:bg-warning data-[state=checked]:border-warning"
          />
        </div>

        {settings.use_trailing_trend_exit && (
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border/50">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-muted-foreground">최소 수익 보존 비율 (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.breakeven_lock_pct ?? 0.2}
                onChange={(e) => updateSettings('breakeven_lock_pct', Number(e.target.value))}
                className="h-8 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[8px] text-muted-foreground">목표가 도달 후 역추세 시 확보할 최저 익절폭</p>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-muted-foreground">최고점 대비 하락 콜백 (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.trailing_callback_pct ?? 0.4}
                onChange={(e) => updateSettings('trailing_callback_pct', Number(e.target.value))}
                className="h-8 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[8px] text-muted-foreground">고점 달성 후 청산할 콜백 비율</p>
            </div>
          </div>
        )}
        <p className="text-[9px] text-muted-foreground leading-relaxed px-1 mt-1 border-t border-border/30 pt-1.5">
          ℹ️ <b>지원 전략:</b> ZL-MACD, MACD-EMA, H-MACD <span className="text-[8px] text-muted-foreground/60">(그 외 전략은 무시됨)</span>
        </p>
      </div>

      {/* Profit Peak Protection */}

      {/* Uptrend Guard (Daily & 4h) — channel_trend / channel_trend2 전용 */}
      {(strategy_type === 'channel_trend' || strategy_type === 'channel_trend2') && (
      <div className="flex flex-col p-3 border border-purple/30 rounded-lg bg-purple/5 space-y-3 mt-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-xs font-bold text-purple">강한 상승 차단 (Uptrend Guard)</Label>
            <p className="text-[10px] text-muted-foreground">가격이 일정 비율 이상 상승했을 때 하단 밴드 진입을 차단합니다.</p>
          </div>
          <Checkbox
            id="uptrend-daily"
            checked={settings.use_uptrend_daily_guard === true}
            onCheckedChange={(checked) => {
              console.log('Uptrend Daily Guard toggled:', checked);
              updateSettings('use_uptrend_daily_guard', checked);
            }}
            className="border-purple/50 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple cursor-pointer"
          />
        </div>

        {settings.use_uptrend_daily_guard && (
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border/50">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-muted-foreground">일일 상승 허용 (%):</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.uptrend_daily_gain_pct ?? 5.0}
                onChange={(e) => updateSettings('uptrend_daily_gain_pct', Number(e.target.value))}
                className="h-8 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-muted-foreground">4시간 상승 허용 (%):</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.uptrend_gain_pct_4h ?? 2.5}
                onChange={(e) => updateSettings('uptrend_gain_pct_4h', Number(e.target.value))}
                className="h-8 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
        )}

        {settings.use_uptrend_daily_guard && (
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <div className="space-y-0.5">
              <Label className="text-xs font-bold text-purple">4시간 ATR 스케일 차단</Label>
              <p className="text-[10px] text-muted-foreground">(price‑4h_ago) / ATR_4h 가 이하 값보다 크면 차단</p>
            </div>
            <Checkbox
              checked={settings.use_uptrend_4h_guard === true}
              onCheckedChange={(checked) => {
                console.log('4h ATR Guard toggled:', checked);
                updateSettings('use_uptrend_4h_guard', checked);
              }}
              className="border-purple/50 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple"
            />
          </div>
        )}

        {settings.use_uptrend_4h_guard && (
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border/50">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-muted-foreground">ATR 배수 기준:</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.uptrend_atr_multiple_4h ?? 1.5}
                onChange={(e) => updateSettings('uptrend_atr_multiple_4h', Number(e.target.value))}
                className="h-8 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
        )}
      </div>
      )}
      <div className="flex flex-col p-3 border border-blue-500/30 rounded-lg bg-blue-500/5 space-y-3 mt-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-xs font-bold text-blue-500">수익 정점 보호 (Profit Peak Protection)</Label>
            <p className="text-[10px] text-muted-foreground">Mode 1: 시간 기반 콜백 — Mode 2: 고점 기반 절대 청산</p>
          </div>
          <Checkbox
            checked={settings.use_profit_peak_protection === true}
            onCheckedChange={(checked) => updateSettings('use_profit_peak_protection', checked)}
            className="border-blue-500/50 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
          />
        </div>

        {settings.use_profit_peak_protection && (
          <div className="space-y-3 pt-2 border-t border-border/50">
            <p className="text-[9px] font-bold text-blue-500 uppercase tracking-wider">Mode 1 — 시간 기반 콜백</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-muted-foreground">시간 활성화 수익 (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.profit_peak_activation_pct ?? 0.2}
                  onChange={(e) => updateSettings('profit_peak_activation_pct', Number(e.target.value))}
                  className="h-8 text-xs font-bold border-border rounded-lg bg-background"
                />
                <p className="text-[8px] text-muted-foreground">이 수익%를 n분 이상 유지하면 보호 개시</p>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-muted-foreground">유지 시간 (분)</Label>
                <Input
                  type="number"
                  step="1"
                  value={settings.profit_peak_duration_min ?? 30}
                  onChange={(e) => updateSettings('profit_peak_duration_min', Number(e.target.value))}
                  className="h-8 text-xs font-bold border-border rounded-lg bg-background"
                />
                <p className="text-[8px] text-muted-foreground">이 시간 이상 유지되면 청산 조건 활성화</p>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-muted-foreground">고점 대비 하락 콜백 (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.profit_peak_callback_pct ?? 0.2}
                  onChange={(e) => updateSettings('profit_peak_callback_pct', Number(e.target.value))}
                  className="h-8 text-xs font-bold border-border rounded-lg bg-background"
                />
                <p className="text-[8px] text-muted-foreground">최고점 대비 이만큼 하락 시 청산</p>
              </div>
            </div>
            <p className="text-[9px] text-muted-foreground leading-relaxed px-1">
              예) 최고 +0.4% → +0.1% (drop 0.3%≥0.2%) 청산. +0.3% → +0.2% (drop 0.1%&lt;0.2%) 유지
            </p>

            <p className="text-[9px] font-bold text-orange-500 uppercase tracking-wider mt-2">Mode 2 — 고점 기반 절대 청산</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-muted-foreground">고점 활성화 수익 (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.profit_peak_high_activation_pct ?? 0.5}
                  onChange={(e) => updateSettings('profit_peak_high_activation_pct', Number(e.target.value))}
                  className="h-8 text-xs font-bold border-border rounded-lg bg-background"
                />
                <p className="text-[8px] text-muted-foreground">이 수익%를 찍었던 적 있으면 보호 개시</p>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-muted-foreground">청산 기준 수익 (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.profit_peak_exit_pct ?? 0.0}
                  onChange={(e) => updateSettings('profit_peak_exit_pct', Number(e.target.value))}
                  className="h-8 text-xs font-bold border-border rounded-lg bg-background"
                />
                <p className="text-[8px] text-muted-foreground">0=본절 / 0.5=+0.5% 수익 남기고 청산</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Unified Trailing Stop */}
      <div className="flex flex-col p-3 border border-purple-500/30 rounded-lg bg-purple-500/5 space-y-3 mt-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-xs font-bold text-purple-500">트레일링 스탑 (PnL Trailing Stop)</Label>
            <p className="text-[10px] text-muted-foreground">최고 수익 대비 콜백 시 청산 — 모든 전략 공통</p>
          </div>
          <Checkbox
            checked={settings.use_trailing_stop === true}
            onCheckedChange={(checked) => updateSettings('use_trailing_stop', checked)}
            className="border-purple-500/50 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
          />
        </div>

        {settings.use_trailing_stop && (
          <div className="space-y-3 pt-2 border-t border-border/50">
            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <Label className="text-[10px] font-bold text-muted-foreground">활성화 수익률 (%)</Label>
                <span className="text-xs font-black text-primary">{settings.trailing_activation_pct ?? 0.3}%</span>
              </div>
              <Slider
                min={0.1} max={2.0} step={0.1}
                value={[settings.trailing_activation_pct ?? 0.3]}
                onValueChange={(vals) => updateSettings('trailing_activation_pct', vals[0])}
              />
              <p className="text-[8px] text-muted-foreground font-medium">이 수익률 이상 도달해야 트레일링 활성화</p>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <Label className="text-[10px] font-bold text-muted-foreground">콜백 (%)</Label>
                <span className="text-xs font-black text-primary">{settings.trailing_callback_pct ?? 0.15}%</span>
              </div>
              <Slider
                min={0.05} max={1.0} step={0.05}
                value={[settings.trailing_callback_pct ?? 0.15]}
                onValueChange={(vals) => updateSettings('trailing_callback_pct', vals[0])}
              />
              <p className="text-[8px] text-muted-foreground font-medium">고점 대비 이만큼 하락하면 청산</p>
            </div>
            <p className="text-[9px] text-muted-foreground leading-relaxed px-1 mt-1 border-t border-border/30 pt-1.5">
              ℹ️ <b>지원 전략:</b> Super Hybrid <span className="text-[8px] text-muted-foreground/60">(channel_trend/2 · auto3 · cen_ichi_kewma 는 각 전략 내 자체 트레일링 사용, MACD 계열은 위 Dynamic Trailing Stop 옵션 사용)</span>
            </p>
          </div>
        )}
      </div>

      {/* Sub-Timeframe Early Exit */}
      <div className="flex flex-col p-3 border border-red-500/30 rounded-lg bg-red-500/5 space-y-3 mt-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-xs font-bold text-red-500">하위 프레임 조기 청산 (MTF Early Exit)</Label>
            <p className="text-[10px] text-muted-foreground">봉 마감 전이라도 5분봉에서 반대 크로스 감지 시 손실을 최소화해 조기 청산합니다</p>
          </div>
          <Checkbox
            checked={settings.use_sub_timeframe_early_exit === true}
            onCheckedChange={(checked) => updateSettings('use_sub_timeframe_early_exit', checked)}
            className="border-red-500/50 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
          />
        </div>
        <p className="text-[9px] text-muted-foreground leading-relaxed px-1 mt-1 border-t border-border/30 pt-1.5">
          ℹ️ <b>지원 전략:</b> ZL-MACD <span className="text-[8px] text-muted-foreground/60">(실전/페이퍼 모드에서만 작동, 백테스트 제외)</span>
        </p>
      </div>

      {/* Symbol Specific TP/SL Overrides */}
      <div className="flex flex-col p-3 border border-indigo-500/30 rounded-lg bg-indigo-500/5 space-y-3 mt-3">
        <div className="space-y-0.5">
          <Label className="text-xs font-bold text-indigo-600">코인별 개별 TP/SL 설정 (Symbol Overrides)</Label>
          <p className="text-[10px] text-muted-foreground">특정 코인에 대해 기본 설정을 무시하고 개별 TP/SL(%)을 적용합니다</p>
        </div>
        <div className="space-y-1.5 pt-2 border-t border-border/50">
          <textarea
            value={symbolText}
            onChange={(e) => handleSymbolTextChange(e.target.value)}
            placeholder={"예)\nBTC: 2.0 / 1.0\nETH: 1.5 / 0.8"}
            rows={4}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono font-bold shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
          />
          <p className="text-[9px] text-muted-foreground leading-relaxed px-1">
            형식: <code className="text-indigo-600 font-bold">코인명: 익절% / 손절%</code> (줄바꿈으로 여러 코인 입력 가능)
          </p>
        </div>
      </div>
    </div>
  );
};
